import React, { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Pencil, Check, X } from 'lucide-react'

interface EditableTextFieldProps {
  value: string | null
  onSave: (newValue: string | null) => Promise<void>
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function EditableTextField({
  value,
  onSave,
  placeholder = '',
  className = '',
  disabled = false,
}: EditableTextFieldProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(value || '')
  const [isUpdating, setIsUpdating] = useState(false)

  const handleSave = async () => {
    if (inputValue === value) {
      setIsEditing(false)
      return
    }

    setIsUpdating(true)
    try {
      await onSave(inputValue || null)
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving field:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    setInputValue(value || '')
    setIsEditing(false)
  }

  const handleEdit = () => {
    if (disabled) return
    setIsEditing(true)
  }

  if (!isEditing) {
    return (
      <div
        className={`group flex items-center gap-2 ${disabled ? '' : 'cursor-pointer'} ${className}`}
        onMouseEnter={() => {}}
      >
        <span className="text-xs">{value || '-'}</span>
        {!disabled && (
          <button
            onClick={handleEdit}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
            title="Edit"
          >
            <Pencil className="w-3 h-3 text-gray-500" />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Input
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        disabled={isUpdating}
        className="h-8 text-xs"
        placeholder={placeholder}
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') handleCancel()
        }}
      />
      <button
        onClick={handleSave}
        disabled={isUpdating}
        className="p-1 hover:bg-green-50 rounded disabled:opacity-50"
        title="Save"
      >
        <Check className="w-4 h-4 text-green-600" />
      </button>
      <button
        onClick={handleCancel}
        disabled={isUpdating}
        className="p-1 hover:bg-red-50 rounded disabled:opacity-50"
        title="Cancel"
      >
        <X className="w-4 h-4 text-red-600" />
      </button>
    </div>
  )
}
